import { endpoints } from '@/config/api';
import { apiClient } from '@/lib/apiClient';
import type { AuthResponse, LoginRequest, RegisterRequest, User } from '@/types';

/**
 * Authentication Service - Handles all auth-related API calls
 * Connects to Azure Functions JWT-based authentication backend
 */

export const authService = {
  /**
   * Sign up new user (register)
   */
  async signUp(email: string, password: string, name?: string): Promise<AuthResponse> {
    const requestBody: RegisterRequest = {
      email,
      password,
      name: name ?? email.split('@')[0] ?? 'User', // Default name from email or fallback
    };

    const response = await apiClient.post<AuthResponse>(endpoints.auth.register, requestBody);

    // Set the token in the API client
    apiClient.setToken(response.accessToken);

    // Store tokens in localStorage
    localStorage.setItem('accessToken', response.accessToken);
    localStorage.setItem('refreshToken', response.refreshToken);
    localStorage.setItem('userId', response.userId);

    return response;
  },

  /**
   * Sign in with email and password (login)
   */
  async signIn(email: string, password: string): Promise<AuthResponse> {
    const requestBody: LoginRequest = {
      email,
      password,
    };

    const response = await apiClient.post<AuthResponse>(endpoints.auth.login, requestBody);

    // Set the token in the API client
    apiClient.setToken(response.accessToken);

    // Store tokens in localStorage
    localStorage.setItem('accessToken', response.accessToken);
    localStorage.setItem('refreshToken', response.refreshToken);
    localStorage.setItem('userId', response.userId);

    return response;
  },

  /**
   * Sign out the current user
   */
  async signOut(): Promise<void> {
    // clearToken now also wipes localStorage
    apiClient.clearToken();
  },

  /**
   * Refresh the access token
   */
  async refreshToken(): Promise<AuthResponse> {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await apiClient.post<AuthResponse>(endpoints.auth.refresh, {
      refreshToken,
    });

    apiClient.setToken(response.accessToken);
    localStorage.setItem('accessToken', response.accessToken);
    localStorage.setItem('refreshToken', response.refreshToken);
    localStorage.setItem('userId', response.userId);

    return response;
  },

  /**
   * Validate the current access token
   */
  async validateToken(): Promise<{ valid: boolean; userId: string }> {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      return { valid: false, userId: '' };
    }

    try {
      return await apiClient.get<{ valid: boolean; userId: string }>(endpoints.auth.validate);
    } catch {
      return { valid: false, userId: '' };
    }
  },

  /**
   * Get current user info
   */
  async getCurrentUser(): Promise<User> {
    const validation = await this.validateToken();
    if (!validation.valid) {
      throw new Error('Invalid or expired token');
    }

    const token = localStorage.getItem('accessToken');
    if (!token) throw new Error('No authentication token');

    const payload = this.decodeToken(token);
    if (!payload) throw new Error('Invalid token format');

    const userId = localStorage.getItem('userId') || validation.userId;
    return {
      id: userId,
      email: String(
        payload.email ??
          payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'] ??
          ''
      ),
      name: String(
        payload.unique_name ??
          payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'] ??
          ''
      ),
      createdAt: new Date().toISOString(),
    };
  },

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!localStorage.getItem('accessToken');
  },

  /**
   * Decode a JWT payload without verifying the signature (client-side only).
   * Signature verification happens on the server; here we just read the claims.
   *
   * JWTs use Base64url encoding (RFC 4648 §5), which substitutes '+' → '-' and '/' → '_'
   * and omits padding. The browser's atob() only handles standard Base64, so we must
   * convert before decoding.
   */
  decodeToken(token: string): Record<string, unknown> | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        console.error(
          '[Auth] decodeToken: token does not have 3 parts, parts=',
          parts.length,
          'token prefix=',
          token.substring(0, 20)
        );
        return null;
      }
      // Convert Base64url → standard Base64, then add padding
      const base64 = (parts[1] as string).replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
      // atob gives us a binary (Latin-1) string; we must re-interpret the bytes
      // as UTF-8 (e.g. Danish æøå in the name/email claim) using TextDecoder.
      const binary = atob(padded);
      const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
      return JSON.parse(new TextDecoder().decode(bytes));
    } catch (err) {
      console.error('[Auth] decodeToken threw:', err, 'token prefix=', token.substring(0, 40));
      return null;
    }
  },

  /**
   * Initialize auth state (call on app startup).
   * Validates the stored token server-side via /api/auth/me — the API is the
   * single source of truth for session validity, not client-side JWT decoding.
   */
  async initialize(): Promise<User | null> {
    const token = localStorage.getItem('accessToken');
    if (!token || token === 'undefined' || token === 'null') {
      console.warn('[Auth] initialize: no valid accessToken in localStorage');
      this.signOut(); // clean up any garbage values
      return null;
    }

    try {
      // Set token so the /me request is authenticated
      apiClient.setToken(token);

      // Let the API validate — returns 401 if expired/invalid
      const user = await apiClient.get<{
        id: string;
        email: string;
        name: string;
        createdAt: string;
      }>(endpoints.auth.me);
      console.info('[Auth] initialize: session restored via API for', user.email);
      return {
        id: user.id,
        email: user.email,
        name: user.name,
        createdAt: user.createdAt ?? new Date().toISOString(),
      };
    } catch {
      // Token invalid/expired — try silent refresh before giving up
      console.warn('[Auth] initialize: token rejected by API, attempting silent refresh');
      try {
        const response = await this.refreshToken();
        return {
          id: response.userId,
          email: response.email,
          name: response.name,
          createdAt: new Date().toISOString(),
        };
      } catch (err) {
        console.error('[Auth] initialize: refresh also failed, signing out', err);
        this.signOut();
        return null;
      }
    }
  },
};
